import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, X } from "lucide-react";
import { api, apiError, formatApiError, uploadFiles } from "@/lib/api";
import { usePhotoInputs } from "@/components/shared/PhotoPicker";
import { SignedImg } from "@/components/shared/SignedImg";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

// 254: dělník doplní konec směny, kterou ukončil systém. Jedna volba „Skončil jsem v 15:25 ·
// běžná směna“ (příchod + denní cíl + přestávka), nebo vlastní čas posunem konce po časové ose
// (± 5 min pro přesné doladění). Konec po běžné směně = přesčas a napíše, na čem dělal.
// 258: jako u běžného odchodu i „Co jsi dělal?“ a fotky (vyžaduje-li je firma, povinné).
// Směna dál čeká na vedoucího, oprava se zapíše do historie úprav.
const TZ = "Europe/Prague";
const STEP = 5;
const hmOf = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
const hh = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const ymd = (iso) => new Intl.DateTimeFormat("sv-SE", { timeZone: TZ }).format(new Date(iso));
const dayName = (iso) => new Intl.DateTimeFormat("cs-CZ", { weekday: "long", day: "numeric", month: "numeric", timeZone: TZ }).format(new Date(iso));
const minOf = (iso) => {
  const [h, m] = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: TZ }).format(new Date(iso)).split(":").map(Number);
  return h * 60 + m;
};
const dur = (m) => `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}`;

export function needsEndFix(r) {
  return !!(r && r.auto_closed && r.check_out && r.approval_status !== "approved" && !r.self_declared);
}

export default function ShiftEndFixDialog({ row, open, onOpenChange, onSaved }) {
  const inMin = row ? minOf(row.check_in) : 0;
  const autoIso = row ? row.auto_check_out || row.check_out : null;
  const sameDay = row ? ymd(row.check_in) === ymd(autoIso) : true;
  const autoMin = row ? (sameDay ? minOf(autoIso) : 24 * 60 - STEP) : 0;
  const brk = row?.break_minutes ?? 30;
  const regRaw = row?.regular_end ? minOf(row.regular_end) : inMin + 8 * 60 + brk;
  const reg = Math.min(Math.max(regRaw, inMin + STEP), 24 * 60 - STEP);
  const lo = Math.floor(inMin / 60) * 60;
  const hi = Math.min(24 * 60 - STEP, Math.max(Math.ceil((autoMin + 30) / 60) * 60, reg + 120));

  const [end, setEnd] = useState(reg);
  const [custom, setCustom] = useState(false);
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [upProg, setUpProg] = useState(null);
  const [busy, setBusy] = useState(false);
  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10 - photos.length);
    e.target.value = "";
    if (!files.length) return;
    setUpProg({ done: 0, total: files.length });
    try {
      const { urls, failed } = await uploadFiles(files, { onEach: (done, total) => setUpProg({ done, total }) });
      if (urls.length) setPhotos((p) => [...p, ...urls]);
      if (failed.length) toast.error(`Nenahrálo se ${failed.length} z ${files.length}: ${apiError(failed[0].error)}`);
    } finally { setUpProg(null); }
  };
  const photoPick = usePhotoInputs({ onChange: addPhotos, disabled: !!upProg });
  const track = useRef(null);
  const drag = useRef(false);
  useEffect(() => { if (open) { setEnd(reg); setCustom(false); setNote(""); setPhotos([]); } }, [open, row?.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!row) return null;

  const clamp = (m) => Math.min(hi, Math.max(inMin + STEP, Math.round(m / STEP) * STEP));
  const pct = (m) => ((Math.min(Math.max(m, lo), hi) - lo) / (hi - lo)) * 100;
  const at = (x) => {
    const r = track.current.getBoundingClientRect();
    const m = clamp(lo + ((x - r.left) / r.width) * (hi - lo));
    return Math.abs(m - reg) <= 10 ? reg : m;   // u běžného konce se zachytí
  };
  const set = (m) => { setEnd(clamp(m)); setCustom(true); };
  const over = end > reg || end > autoMin;   // po běžné směně nebo po automatickém odhlášení = přesčas
  const needPhoto = !!row.photos_required && !(row.photos || []).length;
  const ok = (!over || note.trim().length >= 3) && (!needPhoto || photos.length > 0) && !upProg;
  const net = Math.max(0, end - inMin - brk);
  const ticks = [];
  for (let t = Math.ceil(lo / 240) * 240; t <= hi; t += 240) ticks.push(t);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/attendance/${row.id}/fix-end`, { reason: over ? "overtime" : "late_checkout", check_out: `${ymd(row.check_in)}T${hh(end)}`, note: note.trim() || null, photos });
      toast.success("Odesláno vedoucímu", { description: "Směnu schválí stavbyvedoucí." });
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const choice = (on, onClick, label, sub, testid) => (
    <button type="button" onClick={onClick} aria-pressed={on} data-testid={testid}
      className={`min-h-[56px] rounded-[16px] px-4 py-2 text-left transition-colors ${on ? "bg-brand text-white" : "bg-secondary hover:bg-secondary/70"}`}>
      <span className="block text-[15px] font-bold tabular-nums">{label}</span>
      <span className={`block text-[12.5px] ${on ? "text-white/85" : "text-muted-foreground"}`}>{sub}</span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent sheet className="max-w-lg" data-testid="shift-end-fix" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="space-y-1 pr-10">
          <div className="text-[11.5px] font-extrabold uppercase tracking-[.08em] text-destructive">Nebyl jsi odhlášen</div>
          <DialogTitle className="font-heading text-[21px] font-extrabold tracking-tight">Kdy jsi {dayName(row.check_in)} skončil?</DialogTitle>
          <DialogDescription className="text-[13px] text-muted-foreground truncate">{row.project ? `${row.project} · ` : ""}příchod {hmOf(inMin)} · systém ukončil v {hmOf(autoMin)}</DialogDescription>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {choice(!custom && end === reg, () => { setEnd(reg); setCustom(false); }, `Skončil jsem v ${hmOf(reg)}`, `běžná směna · ${dur(reg - inMin - brk)}`, "fix-regular")}
          {choice(custom, () => setCustom(true), "Jiný čas", "posuň konec na ose", "fix-custom")}
        </div>

        <div className="min-w-0">
          <div className="flex items-center justify-center gap-3" data-testid="fix-readout">
            {custom && <button type="button" onClick={() => set(end - STEP)} aria-label="O 5 minut dřív" className="h-10 w-10 rounded-full bg-secondary grid place-items-center"><Minus size={16} /></button>}
            <div className="text-center min-w-[120px]">
              <div className="text-[34px] font-extrabold tabular-nums leading-none">{hmOf(end)}</div>
              <div className={`text-[12.5px] mt-1 font-semibold ${over ? "pp-ink" : "text-muted-foreground"}`}>
                {end > reg ? `přesčas +${dur(end - reg)}` : end < reg ? `dřív o ${dur(reg - end)}` : "běžná směna"} · v práci {dur(net)}
              </div>
            </div>
            {custom && <button type="button" onClick={() => set(end + STEP)} aria-label="O 5 minut později" className="h-10 w-10 rounded-full bg-secondary grid place-items-center"><Plus size={16} /></button>}
          </div>

          {/* časová osa: příchod, automatické odhlášení, táhlem se posouvá konec */}
          <div ref={track} className="relative h-14 mt-3 touch-none select-none cursor-pointer" data-testid="fix-axis"
            onPointerDown={(e) => { drag.current = true; e.currentTarget.setPointerCapture(e.pointerId); set(at(e.clientX)); }}
            onPointerMove={(e) => { if (drag.current) set(at(e.clientX)); }}
            onPointerUp={() => { drag.current = false; }} onPointerCancel={() => { drag.current = false; }}>
            <div className="absolute inset-x-0 top-6 h-2.5 rounded-full bg-secondary" />
            <div className="absolute top-6 h-2.5 rounded-full bg-brand transition-[width] duration-150 motion-reduce:transition-none" style={{ left: `${pct(inMin)}%`, width: `${pct(end) - pct(inMin)}%` }} />
            <div className="absolute top-[18px] h-[26px] w-0.5 bg-muted-foreground/60" style={{ left: `${pct(inMin)}%` }} aria-hidden="true" />
            <div className="absolute top-[18px] h-[26px] border-l-2 border-dashed border-destructive/70" style={{ left: `${pct(autoMin)}%` }} aria-hidden="true" />
            <div className="absolute top-0 text-[11px] font-semibold text-muted-foreground -translate-x-1/2 whitespace-nowrap tabular-nums" style={{ left: `${Math.max(pct(inMin), 8)}%` }}>příchod {hmOf(inMin)}</div>
            <div className="absolute top-0 text-[11px] font-semibold text-destructive -translate-x-1/2 whitespace-nowrap tabular-nums" style={{ left: `${Math.min(pct(autoMin), 90)}%` }}>odhlášen {hmOf(autoMin)}</div>
            <div role="slider" tabIndex={0} aria-label="Konec směny" aria-valuemin={inMin + STEP} aria-valuemax={hi} aria-valuenow={end} aria-valuetext={hmOf(end)} data-testid="fix-handle"
              onKeyDown={(e) => {
                const d = { ArrowLeft: -STEP, ArrowDown: -STEP, ArrowRight: STEP, ArrowUp: STEP, PageDown: -30, PageUp: 30 }[e.key];
                if (d) { e.preventDefault(); set(end + d); }
              }}
              className="absolute top-[15px] h-7 w-7 -ml-3.5 rounded-full bg-card border-[3px] border-brand shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              style={{ left: `${pct(end)}%` }} />
          </div>
          <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums -mt-1">{ticks.map((t) => <span key={t}>{hmOf(t)}</span>)}</div>
        </div>

        <div>
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <label htmlFor="fix-note" className="text-sm font-bold">Co jsi dělal?</label>
            <span className={`text-[12.5px] ${over ? "pp-ink font-semibold" : "text-muted-foreground"}`}>{over ? "povinné u přesčasu" : "nepovinné"}</span>
          </div>
          <Textarea id="fix-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} data-testid="fix-note"
            placeholder="Např. betonáž stropu, čekali jsme na mixy" className="text-base" />
        </div>
        <div>
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <span className="text-sm font-bold">Fotky z práce</span>
            <span className={`text-[12.5px] ${needPhoto ? "pp-ink font-semibold" : "text-muted-foreground"}`}>{needPhoto ? "aspoň 1 — firma to vyžaduje" : "nepovinné"}</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="fix-photos">
            {photos.map((u, i) => (
              <div key={u} className="relative w-16 h-16">
                <SignedImg url={u} alt="fotka z práce" thumb={64} className="w-16 h-16 rounded-[12px] object-cover" />
                <button type="button" onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))} aria-label="Odebrat fotku"
                  className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-foreground text-background grid place-items-center"><X size={12} /></button>
              </div>
            ))}
            {photos.length < 10 && (
              <button type="button" onClick={photoPick.open} disabled={!!upProg} data-testid="fix-add-photo"
                className="w-16 h-16 rounded-[12px] border-[1.5px] border-dashed border-border text-muted-foreground text-[12px] font-semibold leading-tight grid place-items-center tabular-nums disabled:opacity-60">
                {upProg ? `${upProg.done}/${upProg.total}` : <span>+<br />Přidat</span>}
              </button>
            )}
          </div>
          <p className="text-[12.5px] text-muted-foreground mt-1.5">Fotky jdou ke schválení do deníku, stejně jako při běžném odchodu.</p>
          {photoPick.inputs}
        </div>

        <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} className="w-full md:w-auto">Později</Button>
          <Button type="button" onClick={send} disabled={!ok || busy} data-testid="fix-send" className="w-full md:w-auto">
            {busy ? "Odesílám…" : `Odeslat vedoucímu · konec ${hmOf(end)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
